import { Injectable, Inject, Optional } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';

import { AUTH_CONFIG, AuthConfig, DEFAULT_AUTH_CONFIG } from '../auth.config';
import { TokenService } from '../services/token.service';
import { AuthService } from '../services/auth.service';

/**
 * Auth Interceptor
 * - Attaches access token to outgoing requests
 * - Handles 401 errors
 * - Refreshes token & queues pending requests
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private config: AuthConfig;
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private tokenService: TokenService,
    private authService: AuthService,
    @Optional() @Inject(AUTH_CONFIG) config: AuthConfig
  ) {
    this.config = config || DEFAULT_AUTH_CONFIG;
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    console.log(`[Auth] Intercepting ${request.method} ${request.url}`);

    // Skip auth for whitelisted / blacklisted URLs
    if (this.shouldSkipUrl(request.url)) {

      console.log(`[Auth] Skipping (whitelisted): ${request.url}`);
      return next.handle(request);
    }

    // Add token to request
    const token = this.tokenService.getAccessToken();
    if (token) {
      console.log(`[Auth] Attaching token to: ${request.url}`);
      request = this.addToken(request, token);
    } else {
      console.warn(`[Auth] No token found in memory for: ${request.url}`);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          console.log(`[Auth] 401 Unauthorized detected for: ${request.url}`);
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Attach Authorization header
   */
  private addToken(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    const headerName = this.config.headerName;
    const headerValue = `${this.config.tokenPrefix} ${token}`;

    console.log(`[Auth] Adding Header -> ${headerName}: ${headerValue.substring(0, 15)}...`);

    return request.clone({
      setHeaders: {
        [headerName]: headerValue
      }
    });
  }

  /**
   * Handle 401 Unauthorized
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    // No refresh token → logout
    if (!this.tokenService.hasRefreshToken()) {
      this.authService.logout(false);
      return throwError(() => new Error('No refresh token available'));
    }

    // First request that gets 401 triggers refresh
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap(response => {
          const newAccessToken = response?.data?.access_token;

          if (response.success && newAccessToken) {
            this.refreshTokenSubject.next(newAccessToken);
            return next.handle(this.addToken(request, newAccessToken));
          }

          this.authService.logout(false);
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError(err => {
          this.authService.logout(false);
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    }

    // Queue other requests until refresh completes
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token =>
        next.handle(this.addToken(request, token!))
      )
    );
  }

  /**
   * Decide whether request should skip auth
   */
  private shouldSkipUrl(url: string): boolean {
    // Exact endpoints that shouldn't have tokens


    const whitelistedEndpoints = [
      '/login',
      '/register',
      '/forgot-password',
      '/reset-password'
    ];

    // Check if URL ends with any of the whitelisted endpoints
    const isWhitelisted = whitelistedEndpoints.some(p =>
      url.endsWith(p) || url.includes(p + '?')
    );

    if (isWhitelisted) return true;

    // Check for explicit "public" marker or domains
    if (url.includes('ipify.org') || url.includes('ipapi.com')) {
      return true;
    }

    const isBlacklisted = this.config.blacklistedUrls.some(p =>
      url.includes(p)
    );

    return isBlacklisted;
  }
}
